import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RefreshCw, CheckCircle, AlertCircle, Target } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoalWinAudit = ({ onRefresh }) => {
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditResults, setAuditResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const handleRunAudit = async () => {
    setIsAuditing(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/audit/alignment`,
        {},
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      
      setAuditResults(response.data);
      setShowResults(true);
      toast.success('Alignment audit complete!');
    } catch (error) {
      console.error('Audit failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to run audit');
    } finally {
      setIsAuditing(false);
    }
  };

  const handleReassignWin = async (winId, goalId) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.put(
        `${API}/wins/${winId}`,
        { goal_id: goalId },
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      toast.success('Win reassigned!');
      if (onRefresh) onRefresh();
      // Re-run audit
      handleRunAudit();
    } catch (error) {
      toast.error('Failed to reassign win');
    }
  };

  return (
    <>
      <Button
        onClick={handleRunAudit}
        disabled={isAuditing}
        variant="outline"
        className="flex items-center gap-2"
        title="Check that your wins align with your current goals"
      >
        <RefreshCw className={`w-4 h-4 ${isAuditing ? 'animate-spin' : ''}`} />
        {isAuditing ? 'Analyzing...' : 'Refresh Alignment'}
      </Button>

      <Dialog open={showResults} onOpenChange={setShowResults}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Alignment Audit Complete
            </DialogTitle>
          </DialogHeader>

          {auditResults && (
            <div className="space-y-6">
              {/* Overall Score */}
              <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--cream-100)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold" style={{ color: 'var(--navy-900)' }}>
                      Alignment Health Score
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {auditResults.aligned_count} of {auditResults.total_wins} wins aligned with current goals
                    </p>
                  </div>
                  <div className="text-4xl font-bold" style={{ color: 'var(--navy-900)' }}>
                    {auditResults.alignment_percentage}%
                  </div>
                </div>
              </div>

              {/* Strongly Aligned */}
              {auditResults.strongly_aligned?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h3 className="font-bold" style={{ color: 'var(--navy-900)' }}>
                      Strongly Aligned ({auditResults.strongly_aligned.length} wins)
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {auditResults.strongly_aligned.map((item) => (
                      <div key={item.win_id} className="p-4 rounded-lg bg-green-50 border border-green-200">
                        <p className="text-sm font-medium text-gray-900">{item.win_text}</p>
                        <p className="text-xs text-green-700 mt-1">→ {item.goal_name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Needs Review */}
              {auditResults.needs_review?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    <h3 className="font-bold" style={{ color: 'var(--navy-900)' }}>
                      Needs Review ({auditResults.needs_review.length} wins)
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {auditResults.needs_review.map((item) => (
                      <div key={item.win_id} className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                        <p className="text-sm font-medium text-gray-900 mb-2">{item.win_text}</p>
                        <p className="text-sm text-yellow-800 mb-3">💡 {item.suggestion}</p>
                        {item.suggested_goals?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {item.suggested_goals.map((goal) => (
                              <button
                                key={goal.goal_id}
                                onClick={() => handleReassignWin(item.win_id, goal.goal_id)}
                                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                                style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                              >
                                Reassign to: {goal.goal_name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Goals Without Wins */}
              {auditResults.goals_without_wins?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-5 h-5" style={{ color: 'var(--navy-600)' }} />
                    <h3 className="font-bold" style={{ color: 'var(--navy-900)' }}>
                      Goals Without Wins ({auditResults.goals_without_wins.length})
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {auditResults.goals_without_wins.map((goal) => (
                      <div key={goal.goal_id} className="p-4 rounded-lg" style={{ backgroundColor: 'var(--navy-50)' }}>
                        <p className="text-sm font-medium" style={{ color: 'var(--navy-900)' }}>
                          {goal.goal_name}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">No wins logged yet</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Aligned Message */}
              {auditResults.alignment_percentage === 100 && (
                <div className="p-6 rounded-xl text-center" style={{ backgroundColor: 'var(--cream-100)' }}>
                  <p className="text-lg font-bold" style={{ color: 'var(--navy-900)' }}>
                    🎯 Looking good! All your wins align with your goals!
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GoalWinAudit;
